/**
 * Bad Entrepreneur scroll reveal.
 *
 * Fades and slides content into place as it scrolls into view, with the items in a row or
 * list appearing one after another. Covers the Bad Entrepreneur (be-*) sections plus the
 * theme's own sections on every page except the first section on each page.
 *
 * Anything that reaches or passes the viewport is revealed, including content the visitor
 * skipped over by jumping down the page (anchor links, fast scrolling, the End key), so
 * nothing is ever left hidden above the fold.
 *
 * Progressive enhancement: content is only hidden after this script runs and adds
 * `be-reveal-on` to <html>, so it stays visible without JavaScript. Skipped in the theme
 * editor (so editing is never blocked by hidden content) and for visitors who prefer
 * reduced motion.
 * To turn it off, remove the be-reveal.js script tag near the end of layout/theme.liquid.
 */
(() => {
  if (window.__beRevealInit) return;
  window.__beRevealInit = true;

  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (designMode || reduceMotion) return;

  // Elements that animate in. Siblings that match are staggered in order.
  const SELECTORS = [
    '.be-section .be-intro',
    '.be-experts__controls',
    '.be-experts__footer',
    '.be-plans__toggle',
    '.be-plan',
    '.be-services__intro',
    '.be-services__list-item',
    '.be-features__column',
    '.be-testimonials__card',
    '.be-experts__card',
    '.be-steps__item',
    '.be-note__letter',
    '.be-promo__inner',
    '.be-faq__intro',
    '.be-faq__item',
    '.be-cta__inner',
    '.be-parallax-band__copy',
    '.be-steps__actions',
    '.be-page-hero__copy',
    '.be-page-hero__media',
    '.be-newsletter__intro',
    '.be-newsletter__form-wrap',
    '.be-contact__aside',
    '.be-contact__form-card',
    '.be-freelancer__services',
    '.be-freelancer__cta',
    '.be-mpdp__disclosure',
    '.be-dir__intro',
    '.be-dir__how',
    '.be-dir__group-head',
    '.be-dir__card',
    '.be-proof__label',
    '.be-proof__item',
    '.be-proof__more',
    '.be-form__aside',
    '.be-form__card',
    '.be-story',
    '.be-magnet__copy',
    '.be-magnet__media',
    '.be-freelancer__breadcrumbs',
    '.be-freelancer__media',
    '.be-freelancer__head',
    '.be-freelancer__reviews',
    '.be-coll__crumbs',
    '.be-coll__intro',
    '.be-coll__media',
    '.be-coll__head',
    '.be-coll__card',
    '.be-coll__expert',
    '.be-pexpert__photo',
    '.be-pexpert__body',
    '.be-pillar',
    '.be-footer__top',
    '.be-footer__col',
    // Theme grids on every page: product cards, collection and blog cards.
    'main .product-grid > *',
    'main .resource-list > *',
    'main .collection-list > *',
  ];

  // Theme sections on every page (collections, products, freelancer pages, about, contact).
  // Their direct content animates as a whole unless it holds a grid already handled above.
  // The first section on each page stays put so nothing above the fold ever flickers.
  const SECTION_SELECTORS = [
    'main > .shopify-section:not(:first-child):not(:has(.be-section, .be-parallax-band)) .section > *',
    'main > .shopify-section:not(:first-child):not(:has(.be-section, .be-parallax-band)) > :not(.section, .section-background, link, style, script)',
    // Safety net for any Bad Entrepreneur section without its own entry above: its main
    // blocks animate as a whole, so every page gets the effect.
    'main .be-section > .be-wrap > *',
  ];

  // Drop any selector the browser cannot parse (for example :has() in older Safari) so one
  // unsupported selector never disables the whole effect.
  const supported = (list) =>
    list
      .filter((selector) => {
        try {
          document.createDocumentFragment().querySelector(selector);
          return true;
        } catch (error) {
          return false;
        }
      })
      .join(',');

  const SELECTOR = supported(SELECTORS);
  const SECTION_SELECTOR = supported(SECTION_SELECTORS);

  const STAGGER_MS = 90;
  const MAX_STAGGER_STEPS = 6;
  // Reveal once the top of an element is within this share of the viewport height.
  const TRIGGER_POINT = 0.9;

  /** @type {HTMLElement[]} */
  let pending = [];
  let ticking = false;

  document.documentElement.classList.add('be-reveal-on');

  function check() {
    ticking = false;
    const triggerLine = (window.innerHeight || document.documentElement.clientHeight) * TRIGGER_POINT;

    pending = pending.filter((element) => {
      if (!element.isConnected) return false;
      if (element.getBoundingClientRect().top > triggerLine) return true;
      element.classList.add('is-revealed');
      return false;
    });

    if (pending.length === 0) stop();
  }

  function requestCheck() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(check);
  }

  function stop() {
    window.removeEventListener('scroll', requestCheck);
    window.removeEventListener('resize', requestCheck);
  }

  function mark(element, selector) {
    const parent = element.parentElement;
    const siblings = parent ? [...parent.children].filter((child) => child.matches(selector)) : [element];
    const step = Math.min(Math.max(siblings.indexOf(element), 0), MAX_STAGGER_STEPS);

    element.style.setProperty('--be-reveal-delay', `${step * STAGGER_MS}ms`);
    element.classList.add('be-reveal');
    pending.push(element);
  }

  // Content already on screen when the page loads stays put, so nothing flickers in above
  // the fold. Only content further down animates as the visitor scrolls to it.
  const initialLine = (window.innerHeight || document.documentElement.clientHeight) * TRIGGER_POINT;

  function eligible(element) {
    if (!(element instanceof HTMLElement) || element.classList.contains('be-reveal')) return false;
    if (element.getBoundingClientRect().top < initialLine) return false;
    // Never animate an element inside another animated element (avoids double fades).
    if (element.parentElement?.closest('.be-reveal')) return false;
    // Leave sticky and fixed elements alone; a transform would break their positioning.
    const position = getComputedStyle(element).position;
    return position !== 'sticky' && position !== 'fixed';
  }

  function scan(scope) {
    if (SELECTOR) {
      for (const element of scope.querySelectorAll(SELECTOR)) {
        if (eligible(element)) mark(element, SELECTOR);
      }
    }

    if (SECTION_SELECTOR) {
      for (const element of scope.querySelectorAll(SECTION_SELECTOR)) {
        if (!eligible(element) || element.querySelector('.be-reveal')) continue;
        if (element.getBoundingClientRect().height === 0) continue;
        mark(element, SECTION_SELECTOR);
      }
    }

    if (pending.length === 0) return;
    window.addEventListener('scroll', requestCheck, { passive: true });
    window.addEventListener('resize', requestCheck, { passive: true });
    requestCheck();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  } else {
    scan(document);
  }
})();


/**
 * Scroll cue: a round arrow button at the bottom center of the screen that hints there is
 * more below. Clicking it scrolls to the next section. It hides for good after the first
 * scroll, and never shows on pages too short to scroll or inside the theme editor.
 */
(() => {
  if (window.__beScrollCueInit) return;
  window.__beScrollCueInit = true;
  if (window.Shopify && window.Shopify.designMode) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init() {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight > 240;
    if (!scrollable || window.scrollY > 10) return;

    const cue = document.createElement('button');
    cue.type = 'button';
    cue.className = 'be-scroll-cue';
    cue.setAttribute('aria-label', 'Scroll down for more');
    cue.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M10 4v11M5 10.5l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    document.body.appendChild(cue);
    window.requestAnimationFrame(() => cue.classList.add('is-visible'));

    let removed = false;
    const remove = () => {
      if (removed) return;
      removed = true;
      cue.classList.remove('is-visible');
      window.removeEventListener('scroll', onScroll);
      window.setTimeout(() => cue.remove(), 400);
    };
    const onScroll = () => {
      if (window.scrollY > 24) remove();
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    cue.addEventListener('click', () => {
      const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
      const sections = document.querySelectorAll('main > .shopify-section');
      let target = window.innerHeight * 0.85;
      for (const section of sections) {
        const top = section.getBoundingClientRect().top;
        if (top > window.innerHeight * 0.35) {
          target = top + window.scrollY - header;
          break;
        }
      }
      window.scrollTo({ top: target, behavior: reduceMotion ? 'auto' : 'smooth' });
      remove();
    });
  }

  if (document.readyState === 'complete') {
    window.setTimeout(init, 300);
  } else {
    window.addEventListener('load', () => window.setTimeout(init, 300), { once: true });
  }
})();
